"use client";

import Heading from "@afa-shared/afa-components/dist/Heading";
import Text from "@afa-shared/afa-components/dist/Text";
import FeedbackBlock from "@components/FeedbackBlock";
import InsuranceHeroBlock from "@components/InsuranceHeroBlock/InsuranceHeroblock";
import InsuranceInformationArea from "@components/InsuranceInformationArea/InsuranceInformationArea";
import InsuranceSector from "@components/InsuranceInformationArea/InsuranceSector/InsuranceSector";
import PageInformation from "@components/PageInformation/PageInformation";
import PageMetaSection from "@components/PageMetaSection";
import { PrintButton } from "@components/PrintButton";
import Grid from "@components/client/Grid";
import { InsuranceClaimPageType_insuranceClaimPage_personalizedClaimLinksCollection_items } from "@containers/InsuranceClaimPage/queries/contentfulTypes/InsuranceClaimPageType";
import ArticlePageWrapper from "@containers/Layout/PageWrapper/ArticlePageWrapper";
import { getFeedbackSectionIndex, isFeedbackExcludedPath } from "@lib/feedbackEligibility";
import { defaultContext } from "@utils/contextHelper/getInitialContext";
import {
  shouldRemoveBottomMargin,
  shouldRemoveTopMargin,
} from "@utils/layoutHelpers/layoutMarginHelper";
import { getContentAreaMaxWidth } from "@utils/pageHelper";
import { shouldUseReCapcha } from "@utils/reCapchaHelper";
import { NuqsAdapter } from "nuqs/adapters/next/app";
import { Suspense, useEffect, useState } from "react";

import { ISectorToggler, useSectorToggler } from "hooks/zustand/useSectorToggle";
import { PersonalInfo } from "types/PersonalInfo";
import { Context } from "types/context/Context";

import CFContentArea from "../../components/cFContentArea";
import pageStyles from "../../styles/page.module.css";
import {
  InsurancePageType_insurancepage,
  InsurancePageType_insurancepage_personalizedMessageFallback,
} from "./queries/contentfulTypes/InsurancePageType";

interface InsurancePageData extends InsurancePageType_insurancepage {
  urlToClaimPage: string;
  insuranceName: string;
  latestReviewDate: string;
  pageUpdateDate: string;
  authorName: string;
  reviewName: string;
  reviewRole: string;
}

const InsurancePage = (props: any) => {
  const isLoading = useSectorToggler((state: ISectorToggler) => state.isLoading);
  const selectedSector: PersonalInfo = useSectorToggler(
    (state: ISectorToggler) => state.selectedSector,
  );
  const insurancePage: InsurancePageData = props?.pageData;
  const [userSector, setUserSector] = useState<PersonalInfo>(null);
  const [isCoveredByInsurance, setIsCoveredByInsurance] = useState(false);
  const [personalizedMessage, setPersonalizedMessage] = useState<
    Array<InsurancePageType_insurancepage_personalizedMessageFallback>
  >(
    insurancePage?.personalizedMessageFallback
      ? Array.of(insurancePage?.personalizedMessageFallback)
      : [],
  );

  const imageData = insurancePage?.ivMainImage?.[0];
  const urlToInsuranceClaimsPage = insurancePage?.urlToClaimPage;
  const hasHeroContent = !!insurancePage?.heroContentAreaCollection?.items.length;
  const topImage = insurancePage?.topImage?.[0] || null;

  const context: Context = { ...defaultContext, insuranceName: insurancePage?.insuranceName };

  const useReCapcha = shouldUseReCapcha(insurancePage?.mainAreaCollection?.items);
  const removeLayoutTopMargin = shouldRemoveTopMargin(
    insurancePage?.mainAreaCollection?.items,
    hasHeroContent ? true : false,
  );
  const removeLayoutBottomMargin = shouldRemoveBottomMargin(
    insurancePage?.mainAreaCollection?.items,
  );

  const restrictedRichTextBlock = personalizedMessage?.length > 0 && (
    <CFContentArea desktopColumns={1} contentAreaItems={personalizedMessage} context={context} />
  );

  useEffect(() => {
    if (isLoading) return;
    setIsCoveredByInsurance(
      insurancePage?.availableForAudiencesCollection?.items?.some(
        (a: any) => a?.audianceId === selectedSector?.audienceId,
      ),
    );
    const variantRichText = insurancePage?.personalizedMessageVariantsCollection?.items?.filter(
      (a: any) =>
        a?.restrictedRichTextBlock?.audienceCollection?.items?.some(
          (f: any) => f?.audience?.audianceId === selectedSector?.audienceId,
        ),
    );
    if (variantRichText && variantRichText?.length > 0) {
      setPersonalizedMessage(variantRichText);
    } else {
      setPersonalizedMessage(
        insurancePage?.personalizedMessageFallback
          ? Array.of(insurancePage?.personalizedMessageFallback)
          : [],
      );
    }
    setUserSector(selectedSector);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, selectedSector]);

  const links: InsuranceClaimPageType_insuranceClaimPage_personalizedClaimLinksCollection_items[] =
    [
      {
        __typename: "Link",
        linkText: insurancePage?.insuranceClaimButtonText,
        globalLinkItem: null,
        reference: null,
        url: urlToInsuranceClaimsPage,
        newTab: false,
        ikon: "arrow_forward",
        variant: "knapp",
        audienceCollection: null,
      },
    ];

  const pageId = insurancePage?.linkedFrom?.pageCollection?.items?.find((item) => item).sys?.id;

  return (
    <>
      {hasHeroContent && (
        <InsuranceHeroBlock
          heading={insurancePage?.heading}
          subheader={insurancePage?.subheader}
          ingress={insurancePage?.preamble}
          links={insurancePage?.heroContentAreaCollection?.items}
          image={topImage}
          contentfulPreviewEntryId={insurancePage.sys.id}
          context={context}
        />
      )}
      <ArticlePageWrapper
        imageData={null}
        topImageText={""}
        columns={12}
        relatedContentHeading={insurancePage?.relatedContentHeading}
        relatedContentCollection={insurancePage?.relatedContentCollection}
        relatedContentSmallImages={insurancePage?.relatedContentSmallImages}
        context={context}
        useReCapcha={useReCapcha}
        shouldRemoveTopMargin={removeLayoutTopMargin}
        shouldRemoveBottomMargin={removeLayoutBottomMargin}
        contentfulPreviewEntryId={insurancePage.sys.id}
        contentfulPreviewRelatedContentHeadingFieldId={"relatedContentHeading"}
      >
        <div className={pageStyles.wrapperGrid}>
          {!hasHeroContent && (
            <>
              <div className={pageStyles.headingGrid}>
                <Heading
                  variant="h1"
                  removeMargin
                  data-contentful-entry-id={insurancePage.sys.id}
                  data-contentful-field-id="heading"
                >
                  {insurancePage?.heading}
                </Heading>
                {insurancePage?.preamble && (
                  <Text
                    variant={"preamble"}
                    children={insurancePage?.preamble}
                    removeMargin
                    data-contentful-entry-id={insurancePage.sys.id}
                    data-contentful-field-id="preamble"
                  />
                )}
              </div>
              <Suspense>
                <NuqsAdapter>
                  <InsuranceSector
                    sector={userSector?.sector}
                    role={userSector?.role}
                    context={context}
                    state="about"
                  />
                </NuqsAdapter>
              </Suspense>
              <InsuranceInformationArea
                state="about"
                covered={isCoveredByInsurance}
                hasAudience={userSector !== null}
                coveredHeading={insurancePage?.uspHeading}
                notCoveredHeading={insurancePage?.fallbackUspHeading}
                noAudienceFallbackHeading={insurancePage.uspHeading}
                informationMessage={!!restrictedRichTextBlock && restrictedRichTextBlock}
                bulletListItems={insurancePage?.uspar}
                links={links}
                context={context}
                image={imageData}
                contentfulPreviewEntryId={insurancePage.sys.id}
              />
            </>
          )}

          {insurancePage?.mainAreaCollection?.items?.length > 0 && (
            <Grid alignItems="center">
              <Grid container desktop={8}>
                <CFContentArea
                  key={"insurancePage"}
                  desktopColumns={1}
                  mobileColumns={1}
                  tabletColumns={1}
                  contentAreaItems={insurancePage?.mainAreaCollection?.items}
                  maxWidth={getContentAreaMaxWidth(8)}
                  context={context}
                />
              </Grid>
            </Grid>
          )}
          <Grid alignItems="center">
            <Grid container desktop={8}>
              <PageMetaSection>
                {(insurancePage?.authorName ||
                  insurancePage?.reviewName ||
                  insurancePage?.reviewRole ||
                  insurancePage?.pageUpdateDate) && (
                  <PageInformation
                    pageId={pageId}
                    pageUpdateDate={insurancePage?.pageUpdateDate}
                    authorName={insurancePage?.authorName}
                    reviewName={insurancePage?.reviewName}
                    reviewRole={insurancePage?.reviewRole}
                  />
                )}
                {!isFeedbackExcludedPath(props?.path) && (
                  <FeedbackBlock
                    index={getFeedbackSectionIndex(props?.pageData)}
                    insuranceName={insurancePage?.insuranceName}
                  />
                )}
                <PrintButton />
              </PageMetaSection>
            </Grid>
          </Grid>
        </div>
      </ArticlePageWrapper>
    </>
  );
};

export default InsurancePage;
